import { useMemo, useEffect, useCallback, useRef } from 'react';
import { useHover } from 'react-use';
import { useQuery, useMutation } from '@apollo/client';
import { useLocation } from 'react-router-dom';

import Button from '../../../components/inputs/Button';
import { useOrder, SET_ATTR, UPDATE_PRODUCTS } from '../../../hooks/useOrder';
import { getProduct } from '../../../helpers/products';
import { AUDIENCE_QUERY } from '../../../queries';
import { ADD_ON_TO_ORDER } from '../../../mutations';
import { useAuth } from '../../../hooks/useAuth';
import {
  calculateProductPrice,
  getProductRate,
} from '../../../helpers/pricing';
import { currency } from '../../../utils';
import { AddOn, identifierAddOnIds } from './tabs';
import classNames from 'classnames';

type AddOnCardProps = AddOn;

export default function AddOnCard({
  header,
  body,
  iconComponent,
  topCaption,
  productKey,
  recordsAvailableFn,
}) {
  const { state, dispatch } = useOrder();
  const { products, orderDetails, orderStatus } = state;
  const { orderId, audienceName } = orderDetails;

  const location = useLocation();
  const identifiersInitialState =
    location?.state?.identifiersInitialState || {};
  const addToOrderInitially = identifiersInitialState[productKey];

  const { user } = useAuth();
  const plan = user?.plan;

  const { id: addOnProductId, priceId } = getProduct(productKey, plan);

  const { id: identifierBundleAddOnId } = getProduct(
    'identifierBundleAddOn',
    plan
  );

  const foundAddOnProduct = useMemo(
    () => products?.find((p) => p.id === addOnProductId),
    [addOnProductId, products]
  );

  const disabled = useMemo(() => {
    if (
      !identifierAddOnIds.includes(addOnProductId) &&
      addOnProductId !== identifierBundleAddOnId
    ) {
      return false;
    }

    if (
      products?.find(({ id }) => id === identifierBundleAddOnId) &&
      addOnProductId !== identifierBundleAddOnId
    ) {
      return true;
    }

    if (
      products.find(({ id }) => identifierAddOnIds.includes(id)) &&
      !products.find(({ id }) => id === identifierBundleAddOnId) &&
      addOnProductId === identifierBundleAddOnId
    ) {
      return true;
    }

    if (foundAddOnProduct) {
      return true;
    }

    return false;
  }, [addOnProductId, foundAddOnProduct, identifierBundleAddOnId, products]);

  const { loading, data } = useQuery(AUDIENCE_QUERY, {
    variables: { audienceName },
    fetchPolicy: 'network-only',
  });

  const [
    addOnToOrder,
    {
      loading: loadingAddOnToOrder,
      error: errorAddOnToOrder,
      data: dataAddOnToOrder,
    },
  ] = useMutation(ADD_ON_TO_ORDER);

  useEffect(() => {
    dispatch({
      type: SET_ATTR,
      payload: {
        name: 'loadingAddOnToOrder',
        value: loadingAddOnToOrder,
      },
    });
  }, [dispatch, loadingAddOnToOrder]);

  useEffect(() => {
    dispatch({
      type: SET_ATTR,
      payload: {
        name: 'errorAddOnToOrder',
        value: errorAddOnToOrder,
      },
    });
  }, [dispatch, errorAddOnToOrder]);

  const recordsAvailable = useMemo(() => {
    if (data && data.audience) {
      return recordsAvailableFn(data.audience);
    }
    return 0;
  }, [data, recordsAvailableFn]);

  const addOnPrice = useMemo(
    () => calculateProductPrice(productKey, recordsAvailable, plan),
    [recordsAvailable, productKey, plan]
  );

  const handleClickIncludeAddOn = useCallback(
    ({ force }: { force?: boolean } = {}) => {
      if (
        !foundAddOnProduct &&
        (!disabled || force) &&
        !products.find((p) => p.id === addOnProductId) &&
        !loadingAddOnToOrder
      ) {
        addOnToOrder({
          variables: {
            orderId,
            product: {
              id: addOnProductId,
              priceId,
              quantity: recordsAvailable,
            },
          },
          onCompleted: (data) => {
            dispatch({
              type: UPDATE_PRODUCTS,
              payload: {
                lineItems: data.addOnToOrder.invoice.lineItems,
                totalPrice: data.addOnToOrder.invoice.total,
              },
            });
          },
        });
      }
    },
    [
      foundAddOnProduct,
      disabled,
      products,
      loadingAddOnToOrder,
      addOnProductId,
      addOnToOrder,
      orderId,
      priceId,
      recordsAvailable,
      dispatch,
    ]
  );

  // if all three identifiers are added, add the bundle
  useEffect(() => {
    if (
      addOnProductId === identifierBundleAddOnId &&
      !products.find(({ id }) => id === identifierBundleAddOnId) &&
      identifierAddOnIds.every((identifierAddOnId) =>
        products.find((p) => p.id === identifierAddOnId)
      ) &&
      !loadingAddOnToOrder
    ) {
      handleClickIncludeAddOn({ force: true });
    }
  }, [
    addOnProductId,
    handleClickIncludeAddOn,
    identifierBundleAddOnId,
    loadingAddOnToOrder,
    products,
  ]);

  const addedToOrderInitially = useRef<boolean | null>();

  // handle adding identifiers based on incoming initial state
  useEffect(() => {
    if (
      addedToOrderInitially.current == null &&
      identifierAddOnIds.includes(addOnProductId) &&
      orderStatus != null &&
      orderId != null &&
      !loadingAddOnToOrder
    ) {
      if (addToOrderInitially && !addedToOrderInitially.current) {
        handleClickIncludeAddOn({ force: true });
        addedToOrderInitially.current = true;
      }
    }
  }, [
    addOnProductId,
    addToOrderInitially,
    addedToOrderInitially,
    handleClickIncludeAddOn,
    loadingAddOnToOrder,
    orderId,
    orderStatus,
  ]);

  const element = (hovered) => {
    const isHoveredOrAdded = hovered || foundAddOnProduct;
    const IconComponent = iconComponent({});
    return (
      <div
        className={classNames(
          'relative flex flex-col h-full cursor-pointer',
          topCaption ? 'mt-10 xl:mt-0' : '',
          foundAddOnProduct || disabled || loading || loadingAddOnToOrder
            ? 'pointer-events-none'
            : ''
        )}
        onClick={() => handleClickIncludeAddOn()}
      >
        {(loading || loadingAddOnToOrder) && (
          <div className="absolute -inset-1 z-50 opacity-90 animate-pulse rounded-half bg-gray-100"></div>
        )}
        {topCaption && (
          <div className="absolute -top-10 w-full flex justify-center">
            <div className="flex flex-col">
              <p className="text-center">{topCaption}</p>
              <div className="h-0.5 bg-background-dark w-full mx-auto rounded"></div>
            </div>
          </div>
        )}
        <div
          className={classNames(
            'flex flex-col justify-between flex-grow rounded-half bg-gray-100 p-5 filter transition-all duration-100',
            hovered ? 'drop-shadow-xl bg-gray-200' : '',
            disabled
              ? 'bg-gray-100 border-gray-300 text-gray-300 select-none filter grayscale'
              : ''
          )}
        >
          <div>
            <div
              className={classNames(
                `mb-3 h-3 2xl:h-auto ${disabled ? 'opacity-20' : ''}`,
                isHoveredOrAdded && 'text-primary-dark',
                !isHoveredOrAdded && 'text-secondary'
              )}
            >
              {IconComponent}
            </div>
            <h4 className="mb-1 2xl:mb-3">{header}</h4>
          </div>
          <div className="flex flex-col flex-grow justify-between space-y-2">
            {body}
            <div className="flex flex-col">
              <p className="font-bold mt-2">
                {currency(getProductRate(productKey, plan), 2, 4)} / record
              </p>
              <p>{recordsAvailable?.toLocaleString()} Records</p>
              <p>{currency(addOnPrice)} Total Price</p>
            </div>
          </div>
        </div>
        <div className="flex flex-shrink">
          <Button
            className={`w-full mt-3 ${
              hovered ? `border-primary text-primary` : ''
            }`}
            type="button"
            disabled={foundAddOnProduct || disabled}
          >
            Add To Order
          </Button>
        </div>
      </div>
    );
  };

  const [hoverable] = useHover(element);

  return hoverable;
}
